import { useState } from 'react';
import { Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import { SegmentedControl } from '@/components/molecules/SegmentedControl';
import {
  MemberBudgetEditModal,
  type MemberBudgetEditModalTarget,
} from '@/components/molecules/org-admin/MemberBudgetEditModal';
import {
  OrgAdminEmptyState,
  OrgAdminErrorState,
} from '@/components/molecules/org-admin/OrgAdminStates';
import { useContainerWidth } from '@/components/molecules/org-admin/OrgAdminTable';
import { Section } from '@/components/molecules/org-admin/OrgAdminSection';
import type { OrgAdminSelfContext } from '@/components/molecules/org-admin/types';
import {
  useOrgUsage,
  useOrgUsageReport,
  useOrgUsageServiceReportByUsers,
  useSetMemberBudget,
} from '@/hooks/org-admin';
import { useUsageAnalysis } from '@/hooks/org-admin/useUsageAnalysis';
import { USAGE_MONTH_OPTIONS, useReportingMonth } from '@/hooks/org-admin/useReportingMonth';
import type { OrgUsageReportMember, SetMemberBudgetInput } from '@/services/org-admin';
import { memberAllowance } from '@/services/org-admin/allowance';
import { OrgUsageStatRail, type StatSpec } from './OrgUsageStatRail';
import { SpendSummaryPanel } from './SpendSummaryPanel';
import { ByFeatureSection } from './usage/ByFeatureSection';
import { ByMemberSection } from './usage/ByMemberSection';
import { TrendSection } from './usage/TrendSection';
import { formatCurrencyUsd, labelForCycle } from './usageFormat';

/**
 * Usage — where the money goes. The merged surface that was Usage and Analysis.
 *
 * WHY THEY WERE ONE THING ALL ALONG. The two tabs read the same two backends, shared this tab's
 * reporting range through the URL, rendered the same `OrgUsageStatRail` component with different
 * measures, and each carried a link to the other — Usage had "View analysis", Analysis had
 * "Per-member ledger". When two destinations each need a signpost to the other, they are one
 * destination that has been cut in half. Between them they showed per-member spend twice and
 * per-service cost twice, under four titles.
 *
 * The split was never a split by QUESTION. It was a split by presentation format — cards and tables
 * on one tab, charts on the other — and nobody navigates by chart type. The panel already solves
 * format at the right altitude: every `ChartCard` carries a Chart ⇄ Values toggle rendering the same
 * rows as a real table.
 *
 * The merge is not a negotiation between equals. Analysis had one dominant figure, four dollar
 * figures and a stated reading order; Usage had twenty-four dollar figures and no headings at all.
 * So this adopts Analysis's hierarchy and pours Usage's data into it:
 *
 *   1. **How exposed are we?** — one hero on the budget cycle, budget standing, runway.
 *   2. **Is it growing?**      — the daily trend and the cumulative projection.
 *   3. **Who is spending it?** — the canonical per-member table, its drill-down, automation, and the
 *                                distribution that summarises it.
 *   4. **What is it going on?** — the canonical per-feature table.
 *
 * Ordered by consequence, and every section is a real heading: two of the panel's four screens
 * previously exposed no headings at all, which left a screen-reader user with no document outline.
 */

export interface UsagePanelProps {
  self: OrgAdminSelfContext;
}

export function UsagePanel({ self }: UsagePanelProps) {
  const isAdmin = self.canManage;
  /*
   * ONE range control for the whole tab, and it is the SAME one Overview uses — `useReportingMonth`.
   *
   * `dev` dropped days mode from this panel deliberately: with only months left, a member's
   * `spend_usd_window` for the current month IS their `spend_usd_cycle`, which is what made the old
   * window/cycle toggle in the member table redundant. Since this tab now also holds what was the
   * Analysis view, that decision has to cover the summary and trend too — so `useUsageAnalysis`
   * accepts a month and simply reports no period-over-period delta, which the summary states in
   * words. Two range controls on one screen would be the mixed-clock problem in a new costume.
   */
  const [selection, setSelection] = useReportingMonth();
  const [budgetTarget, setBudgetTarget] = useState<MemberBudgetEditModalTarget | null>(null);

  const { ref: bodyRef, width } = useContainerWidth<HTMLDivElement>();

  /*
   * The derived model for the whole screen. Composes existing query keys and adds no endpoint.
   */
  const analysis = useUsageAnalysis(selection, { enabled: isAdmin });

  /*
   * The raw member rows, for the canonical per-member table.
   *
   * Same query key `useUsageAnalysis` already mounts for this range, so react-query serves it from
   * the same cache entry — this is a second subscription, not a second request. The analysis model
   * carries a derived `byPerson`, but the table needs the wire rows: it reads `blocked`, both token
   * splits and both spend clocks, and shaping all of that into the derived row would make the model
   * a copy of the payload.
   */
  const report = useOrgUsageReport(selection, { enabled: isAdmin });
  const serviceByUsers = useOrgUsageServiceReportByUsers(selection, { enabled: isAdmin });
  /*
   * The org rollup, for the non-admin view only — and now fetched only for them.
   *
   * A plain member sees their organization's cycle spend and member count and nothing else — the
   * behaviour this tab already had. Everything below is gated on `isAdmin` because every report
   * endpoint behind it authorises owner-or-admin and 403s anyone else.
   *
   * `enabled: !isAdmin` because this was called unconditionally: an admin fetched `/usage` on every
   * visit and then rendered none of it, which is both a wasted request and a SECOND spend figure on
   * a different clock. `/usage` is cycle spend (`live_cycle_spend/2` — the max of LiteLLM's counter
   * and our events) and the report below is the selected month; measured on dev 2026-09-11 they
   * were $1.5718689809 and $1.55846944 at the same moment for the same org. Admins now read one
   * source for this tab, the month-scoped report, and a member still reads the only endpoint the
   * backend lets them have (`/usage/report` is owner-or-admin; see `useOrgUsageReport`).
   */
  const rollup = useOrgUsage({ enabled: !isAdmin });

  const setBudget = useSetMemberBudget();

  const rangeLabel = analysis.data?.window.label ?? 'this month';

  const handleBudgetSubmit = async (input: SetMemberBudgetInput) => {
    if (!budgetTarget) return;
    try {
      await setBudget.mutateAsync({ userId: budgetTarget.user_id, input });
      toast.success('Plan allowance updated');
      setBudgetTarget(null);
    } catch (err) {
      toast.error((err as Error).message || 'Failed to update budget');
    }
  };

  const handleEditBudget = (member: OrgUsageReportMember) => {
    setBudgetTarget({
      user_id: member.user_id,
      email: member.email,
      budget_usd: member.budget_usd,
      allowance: memberAllowance(member),
    });
  };

  // ── Non-admin ─────────────────────────────────────────────────────────────

  const nonAdminStats: StatSpec[] = [
    {
      label: labelForCycle('Spend'),
      value: formatCurrencyUsd(rollup.data?.total_spend_usd ?? 0),
    },
    { label: 'Active members', value: String(rollup.data?.member_count ?? 0) },
  ];

  if (!isAdmin) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="flex flex-col gap-3 p-[var(--oa-gutter)]">
            {rollup.isError ? (
              <section className="oa-panel">
                <OrgAdminErrorState
                  title="Couldn't load usage"
                  error={rollup.error}
                  onRetry={() => void rollup.refetch()}
                  className="min-h-[160px]"
                />
              </section>
            ) : (
              <OrgUsageStatRail
                stats={nonAdminStats}
                isLoading={rollup.isLoading}
                skeletonCount={2}
                ariaLabel="Usage summary"
              />
            )}
            {/*
              Said rather than hidden. The per-member and per-feature reports need admin authority,
              and hiding the tab would make a shared link dead-end with no explanation.
            */}
            <div className="oa-panel">
              <OrgAdminEmptyState
                icon={Lock}
                title="Per-member and per-feature usage needs admin authority"
                description="Organization-wide spend reporting is available to owners and admins. Ask an owner if you need access."
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Admin ─────────────────────────────────────────────────────────────────

  const hardError = analysis.isError && report.isError;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/*
        One filter row, above everything it scopes, so the whole screen describes one slice of time.
        The reciprocal "View analysis" / "Per-member ledger" links that used to sit here are gone —
        they were the symptom of the split this merge removes.
      */}
      <div className="oa-toolbar flex flex-wrap items-center justify-between gap-x-3 gap-y-2 py-2">
        <p className="oa-meta min-w-0 flex-1 basis-52 truncate">
          Spend, budgets and cost drivers across the organization.
        </p>
        <SegmentedControl
          ariaLabel="Reporting period"
          value={selection}
          options={USAGE_MONTH_OPTIONS}
          onChange={setSelection}
          size="sm"
        />
      </div>

      <div ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto">
        {hardError ? (
          <div className="p-[var(--oa-gutter)]">
            <div className="oa-panel">
              <OrgAdminErrorState
                title="Couldn't load usage"
                error={analysis.error ?? report.error}
                onRetry={() => {
                  analysis.refetch();
                  void report.refetch();
                }}
                className="min-h-[220px]"
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col p-[var(--oa-gutter)] pb-6">
            {/* 1 · How exposed are we? */}
            <Section title="How exposed are we?">
              <SpendSummaryPanel
                spend={analysis.data?.spend ?? null}
                comparison={analysis.data?.comparison ?? null}
                windowLabel={rangeLabel}
                isLoading={analysis.isLoading}
              />
            </Section>

            {/* 2 · Is it growing? */}
            <TrendSection
              trend={analysis.data?.trend ?? null}
              cumulative={analysis.data?.cumulative ?? null}
              budgetUsd={analysis.data?.spend.budgetUsd ?? 0}
              rangeLabel={rangeLabel}
              width={width}
              isLoading={analysis.isLoading}
              isFetching={analysis.isFetching}
            />

            {/* 3 · Who is spending it? */}
            {report.isError ? (
              <Section title="Who is spending it?">
                <section className="oa-panel">
                  <OrgAdminErrorState
                    title="Couldn't load member usage"
                    error={report.error}
                    className="min-h-[160px]"
                  />
                </section>
              </Section>
            ) : (
              <ByMemberSection
                members={report.data?.members ?? []}
                automation={report.data?.automation}
                serviceByUsers={serviceByUsers.data?.users ?? []}
                concentration={analysis.data?.concentration ?? []}
                selection={selection}
                rangeLabel={rangeLabel}
                width={width}
                isLoading={report.isLoading}
                isServiceByUsersLoading={serviceByUsers.isLoading}
                serviceByUsersError={serviceByUsers.isError ? serviceByUsers.error : null}
                isFetching={analysis.isFetching}
                onEditBudget={handleEditBudget}
              />
            )}

            {/* 4 · What is it going on? */}
            <ByFeatureSection
              rows={analysis.data?.byService ?? []}
              isLoading={analysis.isLoading}
              rangeLabel={rangeLabel}
            />
          </div>
        )}
      </div>

      <MemberBudgetEditModal
        open={!!budgetTarget}
        member={budgetTarget}
        isSubmitting={setBudget.isPending}
        onSubmit={handleBudgetSubmit}
        onClose={() => setBudgetTarget(null)}
      />
    </div>
  );
}
